import React, { useMemo } from "react";
import { connect } from "react-redux";
import { X } from "lucide-react";
import styles from "./CalificationModal.module.scss";
import VoteReputation from "./VoteReputation/VoteReputation";
import { closeCalificationModal } from "../../../store/actions";
import useUserImage from "../../../hooks/useUserImage";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";

type CalificationModalState = {
  isOpen?: boolean;
  userData?: any;
};

type RootState = {
  general?: {
    calificationModal?: CalificationModalState;
  };
};

type CalificationModalProps = {
  onClose?: () => void;
  closeCalificationModal: typeof closeCalificationModal;
  userCalificatedData?: CalificationModalState;
};

const CalificationModal: React.FC<CalificationModalProps> = ({
  onClose,
  closeCalificationModal,
  userCalificatedData,
}) => {
  const isOpen = Boolean(userCalificatedData?.isOpen && userCalificatedData?.userData);
  const targetUser = userCalificatedData?.userData;

  const { imageError, initials, profileImage } = useUserImage(targetUser);

  const displayName = useMemo(() => {
    if (!targetUser) {
      return "";
    }
    if (targetUser.type === "COMPANY") {
      return targetUser.razon_social ?? "Empresa";
    }
    const first = targetUser.name ?? "";
    const last = targetUser.last_name ?? "";
    const full = `${first} ${last}`.trim();
    if (full.length > 0) {
      return full;
    }
    return targetUser.username ?? "Profesional";
  }, [targetUser]);

  const fallbackInitial = displayName ? displayName.charAt(0).toUpperCase() : "U";

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    closeCalificationModal();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleClose}>
        <div className={styles.container}>
          <header className={styles.header}>
            <div className={styles.identity}>
              <div className={styles.avatar}>
                {profileImage && !imageError ? (
                  <img src={profileImage} alt={displayName} />
                ) : (
                  <span>{initials || fallbackInitial}</span>
                )}
              </div>
              <div className={styles.identityInfo}>
                <span className={styles.caption}>Calificar a</span>
                <h2 className={styles.name}>{displayName}</h2>
              </div>
            </div>
            <button type="button" className={styles.closeButton} onClick={handleClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </header>
          <section className={styles.body}>
            <div className={styles.bodyHeader}>
              <h3 className={styles.bodyTitle}>Compartí tu experiencia</h3>
              <p className={styles.bodySubtitle}>
                Seleccioná los rubros, dejá un comentario y elegí una calificación para este perfil.
              </p>
            </div>
            <VoteReputation />
          </section>
        </div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: RootState) => ({
  userCalificatedData: state.general?.calificationModal,
});

export default connect(mapStateToProps, { closeCalificationModal })(CalificationModal);

